import React, { useState, FC, useEffect, useRef } from "react";
import { Input, Select, Button, Radio, Checkbox } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import styles from "./uplift-row.module.css";
import { Icons } from "../vpd-icons/icons";
import { VisibleUpliftTooltip } from "./visible-uplift-tooltip";
import { LibraryUplift, LibraryUplifts, Uplift, UpliftInput, UpliftType } from "../../data-types";

const {
  ["uplift-container"]: upliftContainerClassname,
  ["radio-buttons"]: radioButtonClassname,
  ["name-input"]: nameInputClassname,
  ["amount-input"]: amountInputClassname,
  ["icon"]: iconClassname,
  ["plus"]: plusClassname,
  ["rhs"]: rhsClassname,
  ["lhs"]: lhsClassname,
} = styles as any;

type Props = {
  uplift: Uplift;
  onUpdate: (discount: UpliftInput) => void;
  onDelete: (id: number) => void;
  libraryUplifts: LibraryUplifts;
  autoFocus?: boolean;
  onAutoFocus?: () => void;
  currencySymbol: string;
};

export const UpliftRow: FC<Props> = ({
  libraryUplifts,
  uplift,
  onUpdate,
  onDelete,
  autoFocus = false,
  onAutoFocus,
  currencySymbol,
}) => {
  const upliftInputRef = useRef<any>(null);

  const options = Object.keys(libraryUplifts).map((la) => ({
    label: la,
    options: createOptionGroup(libraryUplifts[la]),
  }));

  const [type, setState] = useState(uplift.type);

  useEffect(() => {
    autoFocus && onAutoFocus && onAutoFocus();
  }, [autoFocus, onAutoFocus]);

  return (
    <div className={upliftContainerClassname}>
      <div className={lhsClassname}>
        <Select
          style={{ width: "100%" }}
          autoFocus={autoFocus}
          placeholder="Name"
          className={nameInputClassname}
          onChange={(_, option) => {
            onUpdate({
              ...uplift,
              baseUpliftId: (option as any).value,
            });
          }}
          options={options}
          defaultValue={uplift.baseUplift?.id}
          showSearch
        />
        <Checkbox
          checked={uplift.includeOnProposal ?? false}
          onChange={(e) => {
            onUpdate({
              ...uplift,
              baseUpliftId: uplift.baseUplift?.id || 0,
              includeOnProposal: e.target.checked,
            });
          }}
        >
          Visible to client on proposal output <VisibleUpliftTooltip></VisibleUpliftTooltip>
        </Checkbox>
      </div>
      <div className={rhsClassname}>
        <Radio.Group
          defaultValue={type}
          buttonStyle="solid"
          onChange={(e) => {
            setState(e.target.value);
            onUpdate({
              ...uplift,
              baseUpliftId: uplift.baseUplift?.id || 0,
              type: e.target.value,
            });
          }}
          className={radioButtonClassname}
        >
          <Radio.Button value={UpliftType.Percent}>percent</Radio.Button>
          <Radio.Button value={UpliftType.Amount}>amount</Radio.Button>
        </Radio.Group>
        {<PlusOutlined className={`${iconClassname} ${plusClassname}`} />}
        <Input
          ref={upliftInputRef}
          onFocus={() => {
            if (upliftInputRef.current) {
              upliftInputRef.current.select();
            }
          }}
          className={amountInputClassname}
          type="number"
          placeholder="Value"
          min="0"
          defaultValue={uplift.amount}
          prefix={type === UpliftType.Amount && currencySymbol}
          suffix={type === UpliftType.Percent && "%"}
          onBlur={(e) => {
            const value = parseFloat(e.target.value);
            if (!Number.isNaN(value) && value !== uplift.amount) {
              onUpdate({
                ...uplift,
                baseUpliftId: uplift.baseUplift?.id || 0,
                amount: value,
              });
            }
          }}
        />
        <Button
          icon={Icons.Delete}
          shape="circle"
          onClick={() => onDelete(uplift.id)}
          type="link"
        />
      </div>
    </div>
  );
};

const createOptionGroup = (libraryUplifts: LibraryUplift[]) =>
  libraryUplifts.map((a) => ({
    label: a.reason,
    value: a.id,
  }));
